import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { Branch, CreateBranchRequest, UpdateBranchRequest } from '@models/file-management/branch/branch.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  addEntity,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  getAll,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { BranchesService } from './branches.service';

type BranchesState = {
  branches: Branch[];
  selectedBranchId: string | null;
  isLoading: boolean;
  branchCreateLoading: boolean;
};

const initialState: BranchesState = {
  branches: [],
  selectedBranchId: null,
  isLoading: false,
  branchCreateLoading: false,
};

const branchesDictionary: StateDictionary<Branch> = {
  entityList: 'branches',
  listLoading: 'isLoading',
  createLoading: 'branchCreateLoading',
  entityId: 'id',
  sortFn: (a: Branch, b: Branch) => a.name.localeCompare(b.name),
  // unused
  selectedEntity: '',
  selectedEntityLoading: '',
};

export const BranchesStore = signalStore(
  withState(initialState),
  withDevtools('branches-store'),
  withComputed(store => ({
    selectedBranch: computed(() => store.branches().find(branch => branch.id === store.selectedBranchId()) || null),
  })),
  withMethods((store, branchesService = inject(BranchesService), router = inject(Router)) => {
    createStateResetHandler<BranchesState>('/file-management/branches')(store, initialState);
    createCustomStateSetOnUrlHandler<BranchesState>('/file-management/branches', { selectedBranchId: null })(store);

    const repositoryMethods = {
      getAll: getAll<Branch>({
        store,
        service: branchesService,
        dictionary: branchesDictionary,
      }),

      addBranch: addEntity<Branch, CreateBranchRequest>({
        store,
        service: branchesService,
        dictionary: branchesDictionary,
        onSuccessCallback: (newBranch: Branch) => {
          patchState(store, { selectedBranchId: newBranch.id });
          router.navigate(['file-management', 'branches', newBranch.id, 'details']);
        },
      }),

      updateBranch: updateEntity<Branch, UpdateBranchRequest>({
        store,
        service: branchesService,
        dictionary: branchesDictionary,
        onSuccessCallback: (updatedBranch: Branch) => {
          patchState(store, { selectedBranchId: updatedBranch.id });
        },
      }),

      deleteBranch: deleteEntity<Branch>({
        store,
        service: branchesService,
        dictionary: branchesDictionary,
        onSuccessCallback: () => {
          patchState(store, { selectedBranchId: null });
          router.navigate(['file-management', 'branches']);
        },
      }),
    };

    const navigationActions = {
      selectBranch: (id: string): void => {
        if (!store.branches().length && !store.isLoading()) repositoryMethods.getAll();
        patchState(store, { selectedBranchId: id });
      },

      goToBranchActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['file-management', 'branches', id]);
        if (!result) return false;

        patchState(store, { selectedBranchId: id });
        return true;
      },

      goToListPage: (): void => {
        patchState(store, { selectedBranchId: null });
        router.navigate(['file-management', 'branches']);
      },

      resetState: (state: Partial<BranchesState>): void => patchState(store, state),
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface BranchesStoreType {
  readonly branches: Signal<Branch[]>;
  readonly isLoading: Signal<boolean>;
  readonly branchCreateLoading: Signal<boolean>;
  readonly selectedBranchId: Signal<string | null>;

  readonly selectedBranch: Signal<Branch | null>;

  readonly getAll: () => void;
  readonly addBranch: (branch: CreateBranchRequest) => void;
  readonly updateBranch: (input: { id: string; dto: UpdateBranchRequest }) => void;
  readonly deleteBranch: (id: string) => void;
  readonly selectBranch: (id: string) => void;
  readonly goToBranchActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
  readonly resetState: (state: Partial<BranchesState>) => void;
}
